import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { apiGet } from '../lib/api';
import {
  LayoutDashboard, Briefcase, FolderKanban, Compass, ShoppingCart, Boxes,
  Wrench, HardHat, ClipboardList, ShieldCheck, Siren, Wallet, Users, Network,
  FileText, Receipt, BarChart3, Settings, Package,
  KanbanSquare, UserCheck, Sparkles, Zap, ShieldAlert, Warehouse, Calculator,
  TrendingDown, ChevronDown, Home, Target, Hammer, Gauge, Coins, UserCog,
  PieChart, CalendarDays, FileSignature, Award, Phone, Bell, Upload, TrendingUp, UsersRound, CheckSquare, UserPlus, BookOpen, ScrollText, Heart, Inbox, Smartphone, ScanFace, Building2, Scale, Calendar, LayoutGrid,
  IndianRupee, Receipt as ReceiptIcon, ScanLine, AlertTriangle, Truck, Fuel, Gauge as GaugeIcon, Droplet, Activity, FileBox,
  FlaskConical, FileWarning, MapPin, ClipboardCheck, FileCheck2, Navigation2, DoorOpen, Plane, Trophy,
  FileSpreadsheet, FileCode, Settings2, Laptop, ArrowLeftRight, Ticket,
  FileBadge, Archive, Banknote, Percent, ReceiptText, BarChart2, RefreshCw,
  Gavel, KeyRound, Clock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SidebarLiveCounts from './SidebarLiveCounts';

// Grouped navigation. Each group has a label, icon, and an array of items.
export const NAV_GROUPS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: Home,
    items: [
      { to: '/app', label: 'Executive Dashboard', icon: LayoutDashboard, end: true, key: 'dashboard', requires_role: ['md','ceo','cfo','super_admin'] },
      { to: '/app/overview/fraud-alerts', label: 'Fraud Alerts', icon: AlertTriangle, key: 'fraud_alerts', requires_role: ['md','ceo','cmd','super_admin'] },
      { to: '/app/approvals', label: 'Approvals Hub', icon: Bell, key: '__public__', badgeKey: 'approvals_count' },
      { to: '/app/hr/me', label: 'My ESS · Attendance & Payslips', icon: UserCog, key: 'dashboard' },
      { to: '/app/hr/apply-leave', label: 'Apply for Leave', icon: Calendar, key: 'dashboard' },
      { to: '/app/hr/my-training', label: 'My Training', icon: Award, key: 'dashboard' },
      { to: '/app/hr/my-assets', label: 'My Assets', icon: Boxes, key: 'dashboard' },
      { to: '/app/hr/my-travel', label: 'My Travel', icon: Plane, key: 'dashboard' },
      { to: '/app/hr/policies', label: 'Company Policies', icon: ScrollText, key: 'dashboard' },
    ],
  },
  {
    id: 'sales',
    label: 'Business Development',
    icon: Target,
    items: [
      { to: '/app/crm/dashboard', label: 'Dashboard', icon: LayoutDashboard, key: 'crm' },
      { to: '/app/crm/contacts', label: 'Contacts', icon: UsersRound, key: 'crm' },
      { to: '/app/crm/companies', label: 'Companies', icon: Briefcase, key: 'crm' },
      { to: '/app/crm/consultants', label: 'Consultants / Architects', icon: Briefcase, key: 'crm' },
      { to: '/app/crm/leads', label: 'Lead Tracker', icon: Users, key: 'crm' },
      { to: '/app/crm/pipeline', label: 'Projects Pipeline', icon: Target, key: 'crm' },
      { to: '/app/crm/pq-tracker', label: 'PQ Tracker', icon: FileCheck2, key: 'crm', badgeKey: 'pq_due_count' },
      { to: '/app/crm/opportunity-finder', label: 'Opportunity Finder · AI', icon: Sparkles, key: 'crm' },
      { to: '/app/crm/competitors', label: 'Competitor Intel · AI', icon: ShieldAlert, key: 'crm' },
      { to: '/app/crm/reports', label: 'MIS Reports', icon: TrendingUp, key: 'crm' },
    ],
  },
  {
    id: 'estimation',
    label: 'Tendering',
    icon: Calculator,
    items: [
      { to: '/app/estimation/dashboard', label: 'Tendering Dashboard', icon: LayoutDashboard, key: 'tendering' },
      { to: '/app/estimation/enquiries', label: 'Enquiry Tracker', icon: ClipboardList, key: 'tendering' },
      { to: '/app/estimation/potential-vendors', label: 'Potential Vendors', icon: Hammer, key: 'tendering' },
      { to: '/app/estimation/rate-library', label: 'Rate Library', icon: BookOpen, key: 'tendering' },
      { to: '/app/estimation/auto-analysis', label: 'Auto Rate · AI', icon: Sparkles, key: 'tendering' },
      { to: '/app/estimation/peb-tonnage', label: 'PEB Tonnage Calc', icon: Scale, key: 'tendering' },
      { to: '/app/estimation/tonnage-ai', label: 'Tonnage · AI', icon: Zap, key: 'tendering' },
      { to: '/app/estimation/master/materials', label: 'Material Master', icon: Building2, key: 'tendering' },
      { to: '/app/estimation/master/labour', label: 'Labour Rates', icon: Hammer, key: 'tendering' },
      { to: '/app/estimation/master/equipment', label: 'Equipment Rates', icon: Wrench, key: 'tendering' },
    ],
  },
  {
    id: 'tasks',
    label: 'Task Management',
    icon: KanbanSquare,
    items: [
      { to: '/app/tasks', label: 'Task Board', icon: KanbanSquare, key: '__public__' },
      { to: '/app/my-tasks', label: 'My Tasks', icon: CheckSquare, key: '__public__' },
      { to: '/app/tasks/inbox', label: 'Dependencies & Approvals', icon: Inbox, key: '__public__' },
      { to: '/app/tasks/report', label: 'Team Task Report', icon: BarChart3, key: '__public__' },
    ],
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: FolderKanban,
    items: [
      { to: '/app/projects', label: 'Project Management', icon: FolderKanban, key: 'projects' },
      { to: '/app/project-planning', label: 'Project Planning · AI', icon: Sparkles, key: 'projects' },
      { to: '/app/risks', label: 'Risk Register', icon: ShieldAlert, key: 'projects' },
      { to: '/app/cost-control', label: 'Cost Control · EVM', icon: TrendingDown, key: 'projects' },
      { to: '/app/projects/hindrances', label: 'Hindrance Register', icon: Siren, key: 'projects' },
      { to: '/app/projects/approvals-tracker', label: 'Approvals Tracker', icon: FileSignature, key: 'projects' },
      { to: '/app/projects/primavera', label: 'Primavera Sync · P6', icon: Network, key: 'projects' },
      { to: '/app/projects/eot-claim-pack', label: 'EOT Claim Pack', icon: FileText, key: 'projects' },
      { to: '/app/engineering', label: 'Engineering & Design', icon: Compass, key: 'engineering' },
      { to: '/app/closeout', label: 'Closeout & Handover', icon: Award, key: 'projects' },
    ],
  },
  {
    id: 'safety',
    label: 'Safety / HSE',
    icon: HardHat,
    items: [
      { to: '/app/safety', label: 'Safety Dashboard', icon: ShieldAlert, key: 'hse', end: true },
      { to: '/app/safety/raise-observation', label: 'Raise Observation', icon: ShieldCheck, key: 'hse' },
      { to: '/app/safety/raise-near-miss', label: 'Report Near-Miss', icon: AlertTriangle, key: 'hse' },
      { to: '/app/safety/toolbox-talks', label: 'Toolbox Talks', icon: Users, key: 'hse' },
      { to: '/app/safety/ppe-compliance', label: 'PPE Compliance', icon: HardHat, key: 'hse' },
    ],
  },
  {
    id: 'quality',
    label: 'Quality',
    icon: Gauge,
    items: [
      { to: '/app/quality', label: 'Quality Dashboard', icon: Gauge, key: 'quality', end: true },
      { to: '/app/quality/cube-tests', label: 'Cube Tests', icon: FlaskConical, key: 'quality' },
      { to: '/app/quality/ncrs', label: 'NCRs', icon: FileWarning, key: 'quality' },
      { to: '/app/quality/inspection-requests', label: 'Inspection Requests', icon: ClipboardCheck, key: 'quality' },
      { to: '/app/quality/snags', label: 'Snags', icon: ClipboardList, key: 'quality' },
      { to: '/app/quality/mtc-register', label: 'MTC Register', icon: FileCheck2, key: 'quality' },
      { to: '/app/quality/capa-effectiveness', label: 'CAPA Effectiveness', icon: ShieldCheck, key: 'quality' },
      { to: '/app/quality/heatmap', label: 'Quality Heatmap', icon: MapPin, key: 'quality' },
    ],
  },
  {
    // Iter 156 — promoted from "Projects" group to its own top-level module.
    // The DPR module is the daily heartbeat of every site, so it deserves
    // a primary slot in the nav instead of being buried 3-clicks deep.
    id: 'dpr',
    label: 'DPR Module',
    icon: ClipboardList,
    items: [
      { to: '/app/dpr', label: 'DPR Console', icon: ClipboardList, key: 'dpr' },
      { to: '/app/dpr/progress', label: 'Progress Reports', icon: BarChart3, key: 'dpr' },
    ],
  },
  {
    // Iter 156 — same promotion for Labour Attendance. It now hosts every
    // workforce surface (live dashboard, master + face DB, today's punches,
    // site-gate kiosk).
    id: 'labour_attendance',
    label: 'Labour Attendance',
    icon: UserCheck,
    items: [
      { to: '/app/labour-dashboard', label: 'Labour Dashboard · Today', icon: BarChart3, key: 'labour_attendance' },
      { to: '/app/labour', label: 'Attendance Register', icon: UserCheck, key: 'labour_attendance' },
      { to: '/app/labour-master', label: 'Labour Master · Face DB', icon: UsersRound, key: 'labour_master' },
      { to: '/app/labour-master/supervisor-performance', label: 'Supervisor Performance', icon: BarChart3, key: 'labour_master', requires_role: ['md','ceo','cmd','cfo','super_admin'] },
      { to: '/app/hr/labour/payment-runs', label: 'Labour Payment Runs', icon: Banknote, key: 'labour_master' },
      { to: '/m/labour', label: 'Site Gate Kiosk · Face', icon: ScanFace, key: 'labour_attendance' },
      { to: '/app/kiosk-security', label: 'Kiosk Security · Audit', icon: ShieldAlert, key: 'labour_attendance', requires_role: ['md','ceo','cmd','cfo','super_admin','admin','hr'] },
      { to: '/app/labour/subcontractor-approvals', label: 'Subcontractor Approvals', icon: ClipboardCheck, key: 'labour_attendance' },
    ],
  },
  {
    id: 'procurement',
    label: 'Procurement & Materials',
    icon: ShoppingCart,
    items: [
      { to: '/app/procurement', label: 'Procurement', icon: ShoppingCart, key: 'procurement' },
      { to: '/app/procurement/material-master', label: 'Material Master', icon: FileBox, key: 'procurement' },
      { to: '/app/procurement/vendors', label: 'Vendor Master', icon: Building2, key: 'procurement' },
      { to: '/app/procurement/vendor-applications', label: 'Vendor Applications', icon: UserCheck, key: 'procurement' },
      { to: '/app/procurement/vendor-change-requests', label: 'Vendor Change Requests', icon: RefreshCw, key: 'procurement' },
      { to: '/app/procurement/vendor-performance', label: 'Vendor Performance', icon: Trophy, key: 'procurement' },
      { to: '/app/procurement/work-orders', label: 'Work Orders', icon: FileSignature, key: 'procurement' },
      { to: '/app/procurement/daily-report', label: 'Daily Report', icon: ClipboardList, key: 'procurement' },
      { to: '/app/mrp', label: 'Material Planning (MRP)', icon: Calculator, key: 'procurement' },
      { to: '/app/material-gaps', label: 'Material Gaps · Alert', icon: TrendingDown, key: 'procurement' },
      { to: '/app/inventory', label: 'Material & Inventory', icon: Boxes, key: 'inventory' },
    ],
  },
  {
    // Promoted out of "Procurement & Materials" to its own top-level module —
    // Stores is a primary surface (GRN, issues, BOQ reconciliation), not a
    // sub-feature of procurement.
    id: 'stores',
    label: 'Store & Inventory',
    icon: Warehouse,
    items: [
      { to: '/app/stores', label: 'Stores & Reconciliation', icon: Warehouse, key: 'inventory' },
      { to: '/app/procurement/material-master', label: 'Material Master', icon: FileBox, key: 'inventory' },
      { to: '/app/stores?tab=mpr', label: 'MPR', icon: FileSignature, key: 'inventory' },
      { to: '/app/stores?tab=po', label: 'Purchase Orders', icon: FileText, key: 'inventory' },
      { to: '/app/stores?tab=grn', label: 'GRN', icon: Truck, key: 'inventory' },
      { to: '/app/stores?tab=mr', label: 'Material Requisitions', icon: Inbox, key: 'inventory' },
      { to: '/app/stores?tab=issues', label: 'Material Issues', icon: ArrowLeftRight, key: 'inventory' },
      { to: '/app/stores?tab=returns', label: 'Returns', icon: ArrowLeftRight, key: 'inventory' },
      { to: '/app/stores?tab=transfers', label: 'Transfers', icon: Truck, key: 'inventory' },
      { to: '/app/stores?tab=vcr', label: 'Vendor Requests', icon: FileWarning, key: 'inventory' },
      { to: '/app/stores?tab=stocktake', label: 'Stocktake', icon: ScanLine, key: 'inventory' },
      { to: '/app/stores?tab=health', label: 'Inventory Health', icon: Activity, key: 'inventory' },
    ],
  },
  {
    id: 'petty_cash',
    label: 'Petty Cash',
    icon: IndianRupee,
    items: [
      { to: '/app/petty-cash',                label: 'Petty Cash Dashboard',  icon: GaugeIcon,    key: 'petty_cash' },
      { to: '/app/petty-cash/expense-request', label: 'Expense Request',       icon: ReceiptIcon,  key: 'petty_cash' },
      { to: '/app/petty-cash/bill-scanner',    label: 'Bill Scanner · AI',     icon: ScanLine,     key: 'petty_cash' },
      { to: '/app/petty-cash/approvals',       label: 'Approvals',             icon: CheckSquare,  key: 'petty_cash' },
      { to: '/app/petty-cash/finance-queue',   label: 'Finance Processing',    icon: Banknote,     key: 'petty_cash' },
      { to: '/app/petty-cash/cash-advances',   label: 'Cash Advances',         icon: Wallet,       key: 'petty_cash' },
      { to: '/app/petty-cash/closures',        label: 'Daily Cash Closure',    icon: CalendarDays, key: 'petty_cash' },
      { to: '/app/petty-cash/vendors',         label: 'Vendor Intelligence',   icon: Building2,    key: 'petty_cash' },
      { to: '/app/petty-cash/reports',         label: 'Reports',               icon: PieChart,     key: 'petty_cash' },
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    icon: Hammer,
    items: [
      { to: '/app/equipment', label: 'Plant & Machinery', icon: Wrench, key: 'equipment' },
      { to: '/app/subcontractors', label: 'Subcontractors', icon: HardHat, key: 'subcontractors' },
    ],
  },
  {
    id: 'fuel_management',
    label: 'Fuel Management',
    icon: Fuel,
    items: [
      { to: '/app/fuel',                label: 'Fuel Dashboard',     icon: GaugeIcon, key: 'fuel' },
      { to: '/app/fuel/vehicles',       label: 'Vehicle Master',     icon: Truck,    key: 'fuel' },
      { to: '/app/fuel/gps-live',       label: 'GPS Live Map',       icon: Navigation2, key: 'fuel', badgeKey: 'gps_alerts' },
      { to: '/app/fuel/equipment',      label: 'Equipment Master',   icon: Wrench,   key: 'fuel' },
      { to: '/app/fuel/refuelling-log', label: 'Refuelling Log',     icon: Activity, key: 'fuel' },
      { to: '/app/fuel/tanks',          label: 'Tank & Storage',     icon: Droplet,  key: 'fuel' },
      { to: '/app/fuel/driver-scores',  label: 'Driver / Operator Scores', icon: UsersRound, key: 'fuel' },
      { to: '/app/fuel/drivers',         label: 'Driver List', icon: UsersRound, key: 'fuel' },
      { to: '/app/fuel/reports',        label: 'Fuel Reports',       icon: PieChart, key: 'fuel' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & Admin',
    icon: Coins,
    items: [
      { to: '/app/finance/overview', label: 'Financial Overview', icon: Wallet, key: 'finance' },
      { to: '/app/finance/payments', label: 'Payments & Receipts', icon: Banknote, key: 'finance' },
      { to: '/app/finance/vendor-invoices', label: 'Vendor Invoices', icon: Receipt, key: 'finance' },
      { to: '/app/finance/client-bills', label: 'Client RA Bills', icon: ReceiptText, key: 'finance' },
      { to: '/app/finance/epc-ledgers', label: 'EPC Ledgers', icon: Scale, key: 'finance' },
      { to: '/app/finance/bg-register', label: 'BG Register', icon: FileBadge, key: 'finance' },
      { to: '/app/finance/tds', label: 'TDS Centre', icon: Percent, key: 'finance' },
      { to: '/app/finance/gst', label: 'GST Centre', icon: ReceiptText, key: 'finance' },
      { to: '/app/finance/cfo', label: 'CFO Pack & Close', icon: BarChart2, key: 'finance' },
      { to: '/app/finance/gl', label: 'General Ledger', icon: BookOpen, key: 'finance' },
      { to: '/app/finance/project-pnl', label: 'Project P&L', icon: Calculator, key: 'finance' },
      { to: '/app/finance/gst-register', label: 'GST Register', icon: FileSpreadsheet, key: 'finance' },
      { to: '/app/finance/tds-register', label: 'TDS Register', icon: FileSpreadsheet, key: 'finance' },
      { to: '/app/finance/tally-export', label: 'Tally Export', icon: FileCode, key: 'finance' },
      { to: '/app/finance/tally-mapping', label: 'Tally Ledger Mapping', icon: Settings2, key: 'finance' },
      { to: '/app/finance/cfo-dashboard', label: 'Cash Flow & Ageing', icon: TrendingUp, key: 'finance' },
      { to: '/app/documents', label: 'Document Mgmt', icon: FileText, key: 'documents' },
      { to: '/app/billing', label: 'Billing & Contracts', icon: Receipt, key: 'billing' },
      { to: '/app/contracts', label: 'Variation & Claims', icon: FileSignature, key: 'billing' },
    ],
  },
  {
    id: 'people',
    label: 'People & HR',
    icon: UserCog,
    items: [
      { to: '/app/hr/dashboard', label: 'HR Dashboard', icon: BarChart3, key: 'hr' },
      { subheader: 'Workforce' },
      { to: '/app/hr/employees', label: 'Employee Master', icon: Users, key: 'hr' },
      { to: '/app/hr/org-chart', label: 'Org Chart', icon: Network, key: 'hr' },
      { to: '/app/hr/onboarding', label: 'Onboarding', icon: UserPlus, key: 'hr' },
      { to: '/app/hr/training', label: 'Training & Certifications', icon: Award, key: 'hr' },
      { to: '/app/hr/assets', label: 'Assets', icon: Boxes, key: 'hr' },
      { to: '/app/hr/travel', label: 'Travel & Expense', icon: Plane, key: 'hr' },
      { to: '/app/hr/documents', label: 'Document Vault', icon: FileSignature, key: 'hr' },
      { to: '/app/hr/exit', label: 'Exit & F&F', icon: UserCog, key: 'hr' },
      { subheader: 'Recruitment' },
      { to: '/app/hr/recruitment', label: 'Dashboard', icon: Briefcase, key: 'hr' },
      { to: '/app/hr/recruitment/jobs', label: 'Job Openings', icon: Briefcase, key: 'hr' },
      { to: '/app/hr/recruitment/pool', label: 'Candidate Pool', icon: Users, key: 'hr' },
      { to: '/app/hr/recruitment/scanner', label: 'Resume Scanner', icon: ScanLine, key: 'hr' },
      { to: '/app/hr/recruitment/matches', label: 'Job Matches', icon: Sparkles, key: 'hr' },
      { to: '/app/hr/recruitment/board', label: 'Internal Job Board', icon: LayoutGrid, key: 'hr' },
      { subheader: 'Time & Attendance' },
      { to: '/app/hr/attendance', label: 'Attendance Roster', icon: CalendarDays, key: 'hr' },
      { to: '/app/hr/attendance-report', label: 'Attendance Report', icon: BarChart3, key: 'hr' },
      { to: '/app/hr/approvals', label: 'Regularization · Approvals', icon: CheckSquare, key: 'hr' },
      { to: '/app/hr/shifts', label: 'Shifts & Roster', icon: CalendarDays, key: 'hr' },
      { to: '/app/hr/holidays', label: 'Holiday Calendar', icon: CalendarDays, key: 'hr' },
      { subheader: 'Payroll & Statutory' },
      { to: '/app/hr/payroll', label: 'Payroll', icon: Wallet, key: 'hr' },
      { to: '/app/hr/salary-master', label: 'Salary Structure Master', icon: Coins, key: 'hr' },
      { to: '/app/hr/ctc', label: 'CTC Structure', icon: Coins, key: 'hr' },
      { to: '/app/hr/reimbursements', label: 'Reimbursements', icon: Receipt, key: 'hr' },
      { to: '/app/hr/salary-advances', label: 'Salary Advances', icon: Wallet, key: 'hr' },
      { to: '/app/hr/it-declaration', label: 'IT Declaration · Form 16', icon: FileSignature, key: 'hr' },
      { to: '/app/hr/statutory', label: 'Statutory Registers', icon: ClipboardList, key: 'hr' },
      { to: '/app/hr/reports', label: 'HR Reports · Data Checks', icon: BarChart3, key: 'hr' },
      { subheader: 'Compliance Tracker' },
      { to: '/app/hr/compliance', label: 'Compliance Dashboard', icon: ShieldCheck, key: 'hr' },
      { to: '/app/hr/compliance/insurance', label: 'Insurance Policies', icon: FileBadge, key: 'hr' },
      { to: '/app/hr/compliance/statutory', label: 'Statutory Compliance', icon: ClipboardCheck, key: 'hr' },
      { to: '/app/hr/compliance/vault', label: 'Compliance Vault', icon: Archive, key: 'hr' },
      { subheader: 'Talent & Benefits' },
      { to: '/app/hr/pms', label: 'Performance · PMS', icon: Award, key: 'hr' },
      { to: '/app/hr/letters', label: 'HR Letters', icon: FileText, key: 'hr' },
      { to: '/app/hr/marriage-gift', label: 'Marriage Gift', icon: Heart, key: 'hr' },
      { to: '/app/hr/referrals', label: 'Employee Referrals', icon: UserPlus, key: 'hr' },
    ],
  },
  {
    id: 'it',
    label: 'IT',
    icon: Laptop,
    items: [
      { to: '/app/it/dashboard', label: 'IT Dashboard', icon: LayoutDashboard, key: 'it_assets' },
      { to: '/app/it/assets', label: 'Asset Register', icon: Laptop, key: 'it_assets' },
      { to: '/app/it/allocations', label: 'Allocations', icon: ArrowLeftRight, key: 'it_assets' },
      { to: '/app/it/requests', label: 'Service Requests', icon: Ticket, key: 'it_assets' },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: Wrench,
    items: [
      { to: '/app/admin/dashboard',    label: 'Admin Dashboard',        icon: LayoutDashboard, key: 'admin_module' },
      { to: '/app/admin/maintenance',  label: 'Facility Maintenance',   icon: Wrench,          key: 'admin_module' },
      { to: '/app/admin/supplies',     label: 'Office Supplies',        icon: Package,         key: 'admin_module' },
      { to: '/app/admin/amc',          label: 'AMC Register',           icon: FileText,        key: 'admin_module' },
      { to: '/app/admin/notices',      label: 'Notices & Circulars',    icon: Bell,            key: 'admin_module' },
      { to: '/app/admin/vendors',      label: 'Admin Vendors',          icon: Users,           key: 'admin_module' },
      { to: '/app/admin/visitors',     label: 'Visitor Management',     icon: UserCheck,       key: 'admin_module' },
      { to: '/app/admin/assets',       label: 'Asset Register',         icon: ClipboardList,   key: 'admin_module' },
      { to: '/app/admin/kpi',          label: 'KPI Dashboard',          icon: TrendingUp,      key: 'admin_module' },
    ],
  },
  {
    id: 'vendor_portal_admin',
    label: 'Vendor Portal Admin',
    icon: ShieldCheck,
    requires_role: ['super_admin'],
    items: [
      { to: '/app/vendor-portal-admin',               label: 'Overview',           icon: LayoutDashboard, key: '__public__' },
      { to: '/app/vendor-portal-admin/accounts',      label: 'Vendor Accounts',    icon: Users,           key: '__public__' },
      { to: '/app/vendor-portal-admin/auctions',      label: 'Auctions',           icon: Gavel,           key: '__public__' },
      { to: '/app/vendor-portal-admin/diagnostics',   label: 'Diagnostics & Logs', icon: Activity,        key: '__public__' },
      { to: '/app/vendor-portal-admin/settings',      label: 'Portal Settings',    icon: Settings,        key: '__public__' },
    ],
  },
  {
    id: 'mis',
    label: 'Analytics & MIS',
    icon: BarChart3,
    items: [
      { to: '/app/mis',          label: 'Exec Dashboard',    icon: LayoutDashboard, key: 'reports' },
      { to: '/app/mis/compare',  label: 'Compare Projects',  icon: BarChart3,       key: 'reports' },
      { to: '/app/mis/reports',  label: 'Saved Reports',       icon: FileSpreadsheet, key: 'reports' },
      { to: '/app/reports/builder', label: 'Custom Report Builder', icon: Sparkles, key: 'reports' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: PieChart,
    items: [
      { to: '/app/reports', label: 'Reports & MIS', icon: BarChart3, key: 'reports' },
      { to: '/app/reports/work-orders-mis', label: 'WO MIS · Exec', icon: ScrollText, key: 'reports' },
      { to: '/app/user-management', label: 'User Management', icon: UsersRound, key: 'settings' },
      { to: '/app/settings', label: 'Settings & Admin', icon: Settings, key: 'settings' },
    ],
  },
];

// localStorage key for per-group expand/collapse state.
const GROUPS_STATE_KEY = 'sidebar_groups_expanded';
// localStorage key for per-user group ordering (Iter 113.2 — P2)
const GROUPS_ORDER_KEY = 'sidebar_groups_order';

export default function Sidebar({ mobileOpen = false, onClose }) {
  const { user } = useAuth();
  const location = useLocation();
  // Unread GPS alerts (idle / geofence) in the last 24h → sidebar badge.
  const [gpsAlerts, setGpsAlerts] = useState(0);
  useEffect(() => {
    let on = true;
    const fetchAlerts = () => apiGet('/gps/alerts', { hours: 24 })
      .then((d) => { if (on) setGpsAlerts(d.unread || 0); })
      .catch(() => {});
    fetchAlerts();
    const t = setInterval(fetchAlerts, 300000); // 5 min
    return () => { on = false; clearInterval(t); };
  }, []);

  // Approvals Hub count — polled every 60s for the sidebar badge.
  const [approvalsCount, setApprovalsCount] = useState(0);
  useEffect(() => {
    if (!user) return;
    let on = true;
    const fetchCount = () => apiGet('/approvals/pending/count')
      .then((d) => { if (on) setApprovalsCount(d.total || 0); })
      .catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { on = false; clearInterval(t); };
  }, [user]);

  // PQ Tracker due/overdue count — polled every 60s for the sidebar badge.
  const [pqDueCount, setPqDueCount] = useState(0);
  useEffect(() => {
    if (!user) return;
    let on = true;
    const fetchCount = () => apiGet('/bd/pq-tracker/count')
      .then((d) => { if (on) setPqDueCount(d.count || 0); })
      .catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { on = false; clearInterval(t); };
  }, [user]);
  const allowed = user?.allowed_modules || NAV_GROUPS.flatMap(g => g.items).filter(i => i.key).map(i => i.key);
  const isAllAccess = Array.isArray(allowed) && allowed.includes('*');

  // role detection — defined before moduleAllowed so super admin can bypass it
  const userRole = (user?.role || '').toLowerCase();
  const userDept = (user?.department || '').toLowerCase();
  const isSuperAdmin = userRole === 'super_admin' || isAllAccess;

  // backwards compat: legacy `labour` permission unlocks both
  // labour_master + labour_attendance sub-modules until admins re-tick them.
  const moduleAllowed = (key) => {
    if (isSuperAdmin) return true;
    if (!key) return false;
    if (isAllAccess) return true;
    if (allowed.includes(key)) return true;
    if ((key === 'labour_master' || key === 'labour_attendance') && allowed.includes('labour')) return true;
    return false;
  };

  // role gate. Some items / groups are role-restricted in
  // addition to module-restricted. Uses lower-case role + dept for matching.
  const roleMatches = (allowedRoles) => {
    if (!allowedRoles || !allowedRoles.length) return true;
    if (isSuperAdmin) return true;
    const set = new Set(allowedRoles.map(r => r.toLowerCase()));
    return set.has(userRole) || set.has(userDept);
  };

  // Filter groups to those with at least one allowed item. Subheaders (items
  // without a `key`) are always kept — they're visual separators only.
  // `key === '__public__'` items are visible to every authenticated
  // user regardless of allowed_modules (used by Task Management).
  const visibleGroups = NAV_GROUPS
    .filter(g => roleMatches(g.requires_role))
    .map(g => ({ ...g, items: g.items.filter(i =>
      (i.subheader || i.key === '__public__' || moduleAllowed(i.key)) &&
      roleMatches(i.requires_role)
    ) }))
    .map(g => ({
      ...g,
      // Drop trailing/leading subheaders and subheaders not followed by any nav item
      items: g.items.filter((i, idx, arr) => {
        if (!i.subheader) return true;
        // Keep subheader only if at least one non-subheader item follows before the next subheader
        for (let j = idx + 1; j < arr.length; j++) {
          if (arr[j].subheader) return false;
          return true;
        }
        return false;
      }),
    }))
    .filter(g => g.items.some(i => !i.subheader));

  // Groups are COLLAPSED by default. The active group (one containing the
  // current route) is force-expanded so users always see the current page in
  // context. Manual toggles persist in localStorage.
  const groupMatchesRoute = (g) =>
    g.items.some(i =>
      !i.subheader && (i.end
        ? location.pathname === i.to
        : location.pathname.startsWith(i.to.split('?')[0]))
    );

  const findActiveGroupId = (expandedMap = {}) => {
    const matching = visibleGroups.filter(groupMatchesRoute);
    if (matching.length === 0) return null;
    if (matching.length === 1) return matching[0].id;
    // Ambiguous route (e.g. Material Master is in both Procurement & Store).
    // Keep the user in whichever matching group is already open.
    const currentlyOpen = matching.find(g => expandedMap?.[g.id] === true);
    return (currentlyOpen || matching[0]).id;
  };

  const [expanded, setExpanded] = useState(() => {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem(GROUPS_STATE_KEY) || '{}'); }
    catch { stored = {}; }
    // Single-expanded mode: only ONE group open at a time. If we have a persisted
    // group that's open, use that; otherwise default to the active route's group.
    const active = findActiveGroupId();
    const persistedOpen = visibleGroups.find(g => stored[g.id] === true)?.id;
    const which = persistedOpen || active;
    const initial = {};
    visibleGroups.forEach(g => { initial[g.id] = (which === g.id); });
    return initial;
  });

  // Auto-expand active group when the route changes (collapses any other group
  // that was previously open — accordion behaviour).
  useEffect(() => {
    const active = findActiveGroupId(expanded);
    if (active && !expanded[active]) {
      setExpanded(prev => {
        const next = {};
        Object.keys(prev).forEach(k => { next[k] = (k === active); });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setExpanded(prev => {
      // Accordion: clicking an already-open group closes it; clicking a closed
      // group opens it AND closes every other group.
      const willOpen = !prev[id];
      const next = {};
      Object.keys(prev).forEach(k => { next[k] = willOpen ? (k === id) : false; });
      localStorage.setItem(GROUPS_STATE_KEY, JSON.stringify(next));
      return next;
    });
  };

  // ─── Iter 146 — Nested sub-section accordion ──────────────────────────
  // Groups may contain {subheader} markers (e.g. People & HR has
  // Workforce / Time & Attendance / Payroll & Statutory). Each
  // subheader-bounded chunk becomes its own collapsible sub-section,
  // accordion-style within the group: clicking one opens it and
  // closes the others in the SAME group.
  // Key shape: `${group.id}::${subheader}` → bool.
  // Items BEFORE the first subheader are NOT inside an accordion — they
  // render directly under the group as today.

  /**
   * Partition a group's items array into:
   *   { leading: [...items before first subheader],
   *     sections: [{ key, label, items }] }
   */
  const partitionGroup = (group) => {
    const leading = [];
    const sections = [];
    let current = null;
    for (const it of group.items) {
      if (it.subheader) {
        current = { key: `${group.id}::${it.subheader}`, label: it.subheader, items: [] };
        sections.push(current);
        continue;
      }
      if (current) current.items.push(it);
      else leading.push(it);
    }
    return { leading, sections };
  };

  // Compute the sub-section that contains the current active route, scoped
  // per group. Returns the section `key` or null.
  const findActiveSectionKey = (group) => {
    const { sections } = partitionGroup(group);
    for (const s of sections) {
      const hit = s.items.some(i =>
        i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)
      );
      if (hit) return s.key;
    }
    return null;
  };

  const [openSections, setOpenSections] = useState({});

  // Auto-open the active sub-section when the route changes. Also auto-open
  // when the parent group itself just opened (so the user sees something
  // rather than three collapsed sub-headers).
  useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev };
      for (const g of visibleGroups) {
        if (!expanded[g.id]) continue;
        const active = findActiveSectionKey(g);
        if (active) {
          // Accordion within group: this section open, all others in same group closed.
          for (const s of partitionGroup(g).sections) {
            next[s.key] = (s.key === active);
          }
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, expanded]);

  const toggleSection = (groupId, sectionKey) => {
    setOpenSections((prev) => {
      const willOpen = !prev[sectionKey];
      const next = { ...prev };
      // Accordion behaviour scoped to THIS group only — clear other sub-
      // sections whose key starts with the same `${groupId}::` prefix.
      const prefix = `${groupId}::`;
      Object.keys(next).forEach((k) => {
        if (k.startsWith(prefix)) next[k] = false;
      });
      next[sectionKey] = willOpen;
      return next;
    });
  };

  // ─── Iter 113.2 — Drag-and-reorder groups (per-user, UI-only) ─────────
  const [groupOrder, setGroupOrder] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(GROUPS_ORDER_KEY) || 'null');
      if (Array.isArray(stored)) return stored;
    } catch {}
    return [];
  });
  const [dragGroupId, setDragGroupId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Order visible groups by user's saved preference. Unknown / new groups
  // keep their natural order at the end.
  const orderedGroups = (() => {
    if (!groupOrder.length) return visibleGroups;
    const map = new Map(visibleGroups.map(g => [g.id, g]));
    const ordered = [];
    for (const id of groupOrder) { if (map.has(id)) { ordered.push(map.get(id)); map.delete(id); } }
    for (const g of visibleGroups) { if (map.has(g.id)) ordered.push(g); }
    return ordered;
  })();

  const handleDragStart = (id) => (e) => {
    setDragGroupId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require dataTransfer data to be set
    try { e.dataTransfer.setData('text/plain', id); } catch {}
  };
  const handleDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };
  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragGroupId || dragGroupId === targetId) {
      setDragGroupId(null); setDragOverId(null); return;
    }
    const currentIds = orderedGroups.map(g => g.id);
    const from = currentIds.indexOf(dragGroupId);
    const to = currentIds.indexOf(targetId);
    if (from < 0 || to < 0) { setDragGroupId(null); setDragOverId(null); return; }
    const next = [...currentIds];
    next.splice(from, 1);
    next.splice(to, 0, dragGroupId);
    setGroupOrder(next);
    localStorage.setItem(GROUPS_ORDER_KEY, JSON.stringify(next));
    setDragGroupId(null); setDragOverId(null);
  };
  const handleDragEnd = () => { setDragGroupId(null); setDragOverId(null); };
  const resetOrder = () => {
    setGroupOrder([]);
    localStorage.removeItem(GROUPS_ORDER_KEY);
  };

  return (
    <>
      {/* Mobile backdrop — only visible when drawer is open on mobile */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        data-testid="sidebar"
        className={`flex flex-col bg-white border-r border-slate-200 w-64 shrink-0 md:translate-x-0 md:relative md:z-auto fixed inset-y-0 left-0 z-50 transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
      <div className="h-16 flex items-center border-b border-slate-200 px-4">
        <img
          src="/assets/ellora-group-blue.png"
          alt="Ellora Group"
          className="h-10 w-auto object-contain"
          data-testid="sidebar-logo"
        />
      </div>
      <SidebarLiveCounts />

      <nav className="flex-1 overflow-y-auto py-3">
        <div className="px-5 mb-2 flex items-center justify-between text-[10px] font-display font-bold uppercase tracking-[0.2em] text-slate-400">
          <span>Modules · {user?.role || 'User'}</span>
          {groupOrder.length > 0 && (
            <button
              type="button"
              onClick={resetOrder}
              className="text-[9px] tracking-[0.15em] text-slate-400 hover:text-[#3b4872] normal-case"
              data-testid="sidebar-reset-order"
              title="Reset sidebar to default order"
            >
              reset order
            </button>
          )}
        </div>

        {orderedGroups.map((group) => {
          const isExpanded = expanded[group.id] === true;
          const navItems = group.items.filter((i) => !i.subheader);
          const groupActive = navItems.some(i =>
            i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)
          );
          const isDragging = dragGroupId === group.id;
          const isDragOver = dragOverId === group.id && dragGroupId && dragGroupId !== group.id;

          return (
            <div
              key={group.id}
              className={`mb-1 transition-opacity ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-t-2 border-[#3b4872]' : ''}`}
              draggable
              onDragStart={handleDragStart(group.id)}
              onDragOver={handleDragOver(group.id)}
              onDragLeave={() => setDragOverId(null)}
              onDrop={handleDrop(group.id)}
              onDragEnd={handleDragEnd}
              data-testid={`sidebar-group-row-${group.id}`}
            >
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                data-testid={`sidebar-group-${group.id}`}
                aria-expanded={isExpanded}
                className={`w-full flex items-center gap-2 px-4 py-2 text-[11px] font-display font-bold uppercase tracking-[0.1em] transition-colors cursor-grab active:cursor-grabbing ${
                  groupActive ? 'text-[#3b4872]' : 'text-slate-500 hover:text-[#0F172A]'
                }`}
                title="Drag to reorder · click to expand/collapse"
              >
                <span className="text-slate-300 select-none" aria-hidden="true">⋮⋮</span>
                <group.icon size={13} className="shrink-0" />
                <span className="flex-1 text-left truncate">{group.label}</span>
                <ChevronDown
                  size={14}
                  className={`shrink-0 transition-transform duration-150 ${isExpanded ? '' : '-rotate-90'}`}
                />
              </button>

              {isExpanded && (() => {
                const { leading, sections } = partitionGroup(group);
                const renderNavLink = (n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => onClose && onClose()}
                    data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 pl-8 pr-5 py-2 text-[13px] transition-colors ${
                        isActive
                          ? 'text-[#0F172A] font-semibold bg-indigo-50/40'
                          : 'text-slate-600 hover:text-[#0F172A] hover:bg-slate-50'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#3b4872]" />}
                        <n.icon size={15} className={isActive ? 'text-[#3b4872]' : 'text-slate-400'} />
                        <span className="truncate">{n.label}</span>
                        {n.badgeKey === 'gps_alerts' && gpsAlerts > 0 && (
                          <span data-testid="gps-alerts-badge" className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                            {gpsAlerts > 99 ? '99+' : gpsAlerts}
                          </span>
                        )}
                        {n.badgeKey === 'approvals_count' && approvalsCount > 0 && (
                          <span data-testid="approvals-hub-badge" className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                            {approvalsCount > 99 ? '99+' : approvalsCount}
                          </span>
                        )}
                        {n.badgeKey === 'pq_due_count' && pqDueCount > 0 && (
                          <span data-testid="pq-due-badge" className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                            {pqDueCount > 99 ? '99+' : pqDueCount}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
                return (
                  <div className="pb-1">
                    {leading.map(renderNavLink)}
                    {sections.map((sec) => {
                      const open = openSections[sec.key] === true;
                      const slug = sec.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                      return (
                        <div key={sec.key}>
                          <button
                            type="button"
                            onClick={() => toggleSection(group.id, sec.key)}
                            aria-expanded={open}
                            data-testid={`sidebar-subsection-${group.id}-${slug}`}
                            className="w-full flex items-center gap-2 px-5 pt-3 pb-1 text-[9px] font-display font-bold uppercase tracking-[0.18em] text-slate-400 hover:text-[#3b4872] transition-colors"
                          >
                            <ChevronDown
                              size={11}
                              className={`shrink-0 transition-transform duration-150 ${open ? '' : '-rotate-90'}`}
                            />
                            <span className="flex-1 text-left">{sec.label}</span>
                          </button>
                          {open && sec.items.map(renderNavLink)}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </nav>
    </aside>
    </>
  );
}
